import React from 'react';
import { CONFIG } from 'constants/index';
import { Switch, Route, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { useAppDispatch, useAppSelector, useResponsive, useSearchParams } from 'client/hooks';
import { selectUserData } from 'client/redux/client/selectors';
import { selectRoomDbData } from 'client/redux/room/selectors';

import 'client/styles/index.scss';

import shareImg from 'client/assets/share.jpg';
import favicon from 'client/assets/favicon/favicon.png';

import { MAIN_PAGE_ROUTE } from 'client/constants/routes';
import NetworkStatus from 'client/components/common/NetworkStatus';

import { useLiveStreamContext } from './LiveStreamConnector/context';
import { LoginWithAuthTokenPopUp } from './common';
import Main from './pages/Main';

import css from './App.module.scss';

const App: React.FC = () => {
  const client = useAppSelector(selectUserData);
  const {
    state: { space },
  } = useLiveStreamContext();
  const appDispatch = useAppDispatch();
  const roomData = useAppSelector(selectRoomDbData);
  const [isMatchingMQ] = useResponsive(['MOBILE', 'TABLET', 'TABLET_L', 'TABLET_P']);
  const searchParams = useSearchParams();
  const isLoginWithAuthTokenVisible = searchParams.get('token') && !client.auth.isLoggedIn;

  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      window.location.reload();
    }
  };

  React.useEffect(() => {
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  const removeRootPreloader = () => {
    const preloadingScreen: HTMLElement | null = document.querySelector('.main-preloader');

    if (preloadingScreen) {
      setTimeout(() => {
        preloadingScreen.style.opacity = '0';
        preloadingScreen.style.visibility = 'hidden';
        preloadingScreen.style.pointerEvents = 'none';
      }, 500);
      setTimeout(() => {
        document?.querySelector('.main-preloader')?.remove();
      }, 1500);
    }
  };

  React.useEffect(() => {
    const checkSignIn = async () => {
      if (isMatchingMQ === undefined) {
        removeRootPreloader();
        return;
      }

      if (!client.uid) {
        console.log('Client is unauthorized...');
      }
    };

    checkSignIn();
  }, [appDispatch, client.uid, isMatchingMQ]);

  React.useEffect(() => {
    if (space?.state) removeRootPreloader();
  }, [space?.state]);

  const renderHelmet = () => {
    const seo = {
      title: `${roomData?.title}`,
      description: `${roomData?.description}`,
    };
    const og = {
      title: `${roomData?.title}`,
      description: `${roomData?.description}`,
      image: shareImg,
    };

    return (
      <Helmet>
        <title>{seo.title}</title>
        <link rel="icon" type="image/png" href={favicon} />
        <link rel="canonical" href={`${CONFIG.domainUrl}/${roomData?._id}`} />
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={og.title} />
        <meta property="og:description" content={og.description} />
        <meta property="og:image" content={og.image} />
      </Helmet>
    );
  };

  return (
    <div className={css.app}>
      {renderHelmet()}
      <NetworkStatus />
      {isLoginWithAuthTokenVisible ? (
        <LoginWithAuthTokenPopUp token={searchParams.get('token')!} />
      ) : (
        <Switch>
          <Route path={MAIN_PAGE_ROUTE}>
            <Main />
          </Route>

          <Redirect to={MAIN_PAGE_ROUTE} />
        </Switch>
      )}
    </div>
  );
};

export default App;
